import { oauthKey, getActiveAccountId, readStoredApiKey } from './accountSession';

/**
 * Get auth headers from localStorage only (no React). Use in axios interceptors and non-React code.
 * Matches AuthContext getAuthHeaders logic: OAuth (agentic_ai_auth, account-scoped) then API key (ai_tech_api_key).
 */
export const parseJwtPayload = (token) => {
  try {
    const parts = String(token || '').split('.');
    if (parts.length < 2) return null;
    const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch (e) {
    return null;
  }
};

// Reads THIS tab's bound OAuth token blob, if any — namespaced by account id
// so two tabs on two different accounts never read each other's credentials.
function readOAuthTokenData() {
  const accountId = getActiveAccountId();
  if (!accountId) return null;
  const raw = localStorage.getItem(oauthKey(accountId));
  if (!raw) return null;
  try {
    return JSON.parse(unescape(atob(raw)));
  } catch (e) {
    return null;
  }
}

/** Read logged-in user profile from OAuth token (firstName, lastName, user_name email). */
export const getAuthUserFromStorage = () => {
  try {
    const tokenData = readOAuthTokenData();
    if (!tokenData) return { firstName: '', lastName: '', email: '' };
    const claims = parseJwtPayload(tokenData?.accessToken) || {};
    return {
      firstName: String(claims.firstName || tokenData.firstName || '').trim(),
      lastName: String(claims.lastName || tokenData.lastName || '').trim(),
      email: String(
        claims.user_name
        || claims.username
        || tokenData.user_name
        || tokenData.username
        || ''
      ).trim(),
    };
  } catch (e) {
    return { firstName: '', lastName: '', email: '' };
  }
};

export const getAuthHeadersFromStorage = (options = {}) => {
  const { useBearer = true } = options;
  try {
    const tokenData = readOAuthTokenData();
    if (tokenData) {
      const token = tokenData?.accessToken;
      const accId = tokenData?.accountId ?? '';
      const appId = tokenData?.appInstanceId ?? '';
      const userId = tokenData?.accountUserId ?? '';
      if (token) {
        return {
          'Content-Type': 'application/json',
          ...(useBearer && { Authorization: `Bearer ${token}` }),
          ...(accId !== '' && { accountId: String(accId) }),
          ...(appId !== '' && { appInstanceId: String(appId) }),
          ...(userId !== '' && { accountUserId: String(userId) }),
        };
      }
    }
    const apiKey = readStoredApiKey();
    if (apiKey) {
      return {
        'X-API-Key': apiKey,
        'Content-Type': 'application/json',
      };
    }
  } catch (e) {
    // ignore
  }
  return {};
};
